<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useConfirm } from '../composables/useConfirm.js';
import { matchesConfirmWord } from '../domain/playerDeletion.js';

/* Hängt einmal in App.vue. Zeigt sich nur, wenn confirm() aufgerufen wurde. */
const { state, answer } = useConfirm();

/* Optional (state.requireText): Der Bestätigungsknopf bleibt gesperrt, bis genau dieses Wort
   eingetippt ist — für Aktionen, die sich nicht rückgängig machen lassen und viel mitnehmen
   (Spieler löschen). Ohne requireText verhält sich der Dialog wie bisher. */
const typed = ref('');
const locked = computed(
  () => !!state.value?.requireText && !matchesConfirmWord(typed.value, state.value.requireText)
);

function onKey(e) {
  if (!state.value) return;
  if (e.key === 'Escape') answer(false);
  /* Enter bestätigt bewusst NICHT: Bei einer Löschabfrage soll man den Knopf
     treffen müssen, nicht versehentlich die Eingabetaste drücken. */
}

watch(state, (open) => {
  typed.value = '';
  if (open) document.addEventListener('keydown', onKey);
  else document.removeEventListener('keydown', onKey);
});

onBeforeUnmount(() => document.removeEventListener('keydown', onKey));
</script>

<template>
  <template v-if="state">
    <div class="confirm-scrim" @click="answer(false)"></div>

    <div class="confirm-box" role="alertdialog" aria-modal="true">
      <div class="cb-title">{{ state.title }}</div>
      <p class="cb-message">{{ state.message }}</p>
      <p v-if="state.detail" class="cb-detail">{{ state.detail }}</p>

      <label v-if="state.requireText" class="cb-require">
        <span>{{ state.requireLabel }}</span>
        <input
          v-model="typed"
          type="text"
          class="cb-input"
          autocomplete="off"
          autocapitalize="off"
          spellcheck="false"
          :aria-label="state.requireLabel"
        />
      </label>

      <div class="cb-actions">
        <button class="btn ghost small" @click="answer(false)">
          {{ $t('common.cancel') }}
        </button>
        <button :class="['btn', 'small', state.danger !== false && 'danger']" :disabled="locked" @click="answer(true)">
          {{ state.confirmLabel || $t('common.delete') }}
        </button>
      </div>
    </div>
  </template>
</template>

<style scoped>
.confirm-scrim {
  position: fixed;
  inset: 0;
  background: rgba(4, 6, 9, 0.62);
  z-index: 40;
}
.confirm-box {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 41;
  width: min(380px, calc(100vw - 40px));
  padding: 22px 24px;
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 14px;
  box-shadow: 0 20px 60px var(--shadow-strong);
}
.cb-title {
  font-family: var(--display);
  font-size: 1rem;
  font-weight: 600;
  color: var(--text);
  margin-bottom: 8px;
}
.cb-message {
  font-size: 0.84rem;
  color: var(--muted);
  line-height: 1.6;
}
.cb-detail {
  font-size: 0.78rem;
  color: var(--amber);
  line-height: 1.5;
  margin-top: 8px;
}
.cb-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 18px;
}
.cb-require {
  display: block;
  margin-top: 14px;
}
.cb-require span {
  display: block;
  font-size: 0.78rem;
  color: var(--text);
  line-height: 1.5;
  margin-bottom: 6px;
}
.cb-input {
  width: 100%;
  padding: 9px 11px;
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: 7px;
  color: var(--text);
  font: inherit;
  font-size: 0.88rem;
}
.cb-input:focus {
  outline: none;
  border-color: var(--red);
}
.btn.danger:disabled {
  opacity: 0.45;
  cursor: default;
}
.btn.danger {
  background: var(--red-bg);
  color: var(--red);
  border: 1px solid var(--red);
}
.btn.danger:hover {
  background: rgba(226, 104, 90, 0.2);
}
</style>
